import React from 'react';
import { Link } from 'react-router-dom';
import netMeteringFlow from '../../assets/meter/net-metering-flow.png';

// Coordinates on the 1280x721 source image, used the same way HowItWorks
// animates signal dots along the image's own cabling: a green pulse for
// solar coming in, a cyan pulse for the exchange with the grid.
const METER = { x: 625, y: 460 };
const SOLAR = { x: 115, y: 460 };
const GRID = { x: 1090, y: 460 };

const FlowOverlay = () => (
  <svg
    className="nm-overlay"
    viewBox="0 0 1280 721"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <line x1={SOLAR.x} y1={SOLAR.y} x2={METER.x} y2={METER.y} className="nm-overlay__pulse nm-overlay__pulse--solar" />
    <line x1={METER.x} y1={METER.y} x2={GRID.x} y2={GRID.y} className="nm-overlay__pulse nm-overlay__pulse--grid" style={{ animationDelay: '0.6s' }} />
  </svg>
);

const METRICS = [
  { key: 'import', label: 'Grid Import', value: '1.6 kW', tone: 'grid' },
  { key: 'export', label: 'Grid Export', value: '2.8 kW', tone: 'solar' },
  { key: 'net', label: 'Net Export', value: '+1.2 kW', tone: 'net' },
];

const FLOW_STEPS = ['Generate', 'Measure', 'Consume', 'Export', 'Calculate'];

const CAPABILITIES = [
  'Energy imported from the grid',
  'Energy exported to the grid',
  'Consumption',
  'Solar generation',
  'Net energy position',
  'Applicable tariffs',
  'Import tariff calculations',
  'Export and feed-in credits, where applicable',
  'Time based tariff structures, where applicable',
  'Customer billing information',
  'Energy transaction history',
];

const NetMeteringSection = () => (
  <section className="gh-section nm" id="net-metering">
    <div className="gh-section__inner">
      <span className="eyebrow">GridX Net Metering</span>
      <h2 className="gh-section__title">Energy in. Energy out. Fully measured.</h2>
      <p className="gh-section__sub">
        The GRIDx meter supports the implementation of net metering. It separates
        energy imported from the grid from energy exported back to it, so solar
        households and utilities both see the true picture.
      </p>

      <div className="nm__diagram">
        <img
          src={netMeteringFlow}
          alt="Diagram showing solar generation flowing through a solar inverter into the GRIDx meter, which measures both energy exported to and imported from the utility grid"
          className="nm__diagram-img"
          width="1280"
          height="721"
        />
        <FlowOverlay />
      </div>

      <div className="nm__metrics" role="group" aria-label="Illustrative net metering readings">
        {METRICS.map((m) => (
          <div key={m.key} className={`nm__metric nm__metric--${m.tone}`}>
            <span className="nm__metric-value">{m.value}</span>
            <span className="nm__metric-label">{m.label}</span>
          </div>
        ))}
      </div>
      <p className="nm__metrics-note">Illustrative dashboard values, not live customer readings.</p>

      <div className="nm__flow" aria-hidden="true">
        {FLOW_STEPS.map((step, i) => (
          <React.Fragment key={step}>
            <span className="nm__flow-step">{step}</span>
            {i < FLOW_STEPS.length - 1 && <span className="nm__flow-arrow">&rarr;</span>}
          </React.Fragment>
        ))}
      </div>

      <div className="nm__billing">
        <h3 className="nm__billing-title">Built for tariff and billing intelligence.</h3>
        <p className="nm__billing-text">
          GRIDx captures the energy data that tariff and billing calculations run on:
          consumption, solar generation, energy imported from the grid, energy
          exported to it, and the resulting net position. That data is captured ready
          to apply against a utility's own tariff structure, whether flat rate, time
          based, or export credit.
        </p>
        <ul className="nm__capabilities">
          {CAPABILITIES.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </div>

      <Link to="/" className="nm__back-link">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Back to Home
      </Link>
    </div>
  </section>
);

export default NetMeteringSection;
